/**
 * PartyKit adapter for AI Framework
 */

import { AIResponse, PersonalityProfile } from '../core/types';
import { ConversationService } from '../services/conversation';

export interface PartyKitMessage {
  type: string;
  payload: any;
}

export interface AIRequestPayload {
  message: string;
  userId: string;
  agentId?: string;
  personality?: PersonalityProfile;
  metadata?: Record<string, any>;
}

export class PartyKitAdapter {
  constructor(private conversationService: ConversationService) {}

  async handleMessage(message: PartyKitMessage): Promise<PartyKitMessage | null> {
    switch (message.type) {
      case 'ai:request':
        return this.handleAIRequest(message.payload);

      case 'ai:clear':
        return this.handleClearConversation(message.payload);

      default:
        return null;
    }
  }

  private async handleAIRequest(payload: AIRequestPayload): Promise<PartyKitMessage> {
    try {
      const response = await this.conversationService.processMessage(
        payload.userId,
        payload.message,
        payload.personality,
        payload.metadata
      );

      return {
        type: 'ai:response',
        payload: {
          success: true,
          response: this.serializeResponse(response),
          userId: payload.userId,
          agentId: payload.agentId,
        },
      };
    } catch (error: any) {
      return {
        type: 'ai:error',
        payload: {
          success: false,
          error: {
            message: error.message,
            code: error.code || 'UNKNOWN_ERROR',
          },
          userId: payload.userId,
          agentId: payload.agentId,
        },
      };
    }
  }

  private handleClearConversation(payload: { conversationId: string }): PartyKitMessage {
    this.conversationService.clearConversation(payload.conversationId);

    return {
      type: 'ai:cleared',
      payload: {
        success: true,
        conversationId: payload.conversationId,
      },
    };
  }

  private serializeResponse(response: AIResponse): any {
    return {
      text: response.text,
      audioUrl: response.audio ? this.createAudioUrl(response.audio) : undefined,
      emotion: response.emotion,
      actions: response.actions,
      metadata: response.metadata,
    };
  }

  private createAudioUrl(audio: ArrayBuffer): string {
    // In production, this would upload to CDN and return URL
    // For now, return base64 data URL
    const base64 = btoa(
      new Uint8Array(audio).reduce((data, byte) => data + String.fromCharCode(byte), '')
    );
    return `data:audio/mp3;base64,${base64}`;
  }

  // Helper method to format outgoing messages
  formatBroadcast(response: AIResponse, roomId: string): PartyKitMessage {
    return {
      type: 'message:ai',
      payload: {
        id: `ai-${Date.now()}`,
        roomId,
        text: response.text,
        audioUrl: response.audio ? this.createAudioUrl(response.audio) : undefined,
        emotion: response.emotion,
        actions: response.actions,
        timestamp: new Date().toISOString(),
        sender: {
          id: 'ai',
          username: 'AI Assistant',
          type: 'ai',
        },
      },
    };
  }
}
